import { fabric } from 'fabric';
import {
  DEFAULT_CANVAS_WIDTH,
  DEFAULT_CANVAS_HEIGHT,
  parseDimensions,
  relinkButtonObjects,
  FABRIC_CUSTOM_PROPS,
} from './layoutBuilder';
import { countButtons, stickerUrl } from './editor/fabricHelpers';
import {
  shouldIncludeInImageUpload,
  isServerUploadUrl,
  sanitizeCanvasForFabricExport,
} from './layoutBuilder';
import { MAX_BUTTONS } from './editor/constants';

fabric.Object.prototype.borderColor = '#2196F3';
fabric.Object.prototype.cornerColor = '#2196F3';
fabric.Object.prototype.transparentCorners = false;

const existingCustomProps = fabric.Object.prototype.customProperties || [];
fabric.Object.prototype.customProperties = [
  ...new Set([...existingCustomProps, ...FABRIC_CUSTOM_PROPS]),
];

function applyLoadedObjectRules(obj) {
  if (obj.name === 'text') {
    if (obj.setControlsVisibility) {
      obj.setControlsVisibility({
        tl: false, tr: false, br: false, bl: false,
        ml: false, mt: false, mr: false, mb: false,
        mtr: obj.niceName === 'text',
      });
    }
  }
  if (obj.name === 'editable' || obj.name === 'entryfield') {
    obj.lockMovementX = true;
    obj.hasControls = false;
  }
  if (obj.niceName === 'orgbanner' || obj.name === 'orgbanner') {
    obj.lockMovementX = true;
    obj.lockMovementY = true;
    obj.hasControls = false;
  }
  if (obj.type === 'group' && obj.getObjects) {
    obj.getObjects().forEach(applyLoadedObjectRules);
  }
}

export function createFabricCanvas(canvasEl, width = DEFAULT_CANVAS_WIDTH, height = DEFAULT_CANVAS_HEIGHT) {
  return new fabric.Canvas(canvasEl, {
    width,
    height,
    backgroundColor: '#FFFFFF',
    selection: true,
    preserveObjectStacking: true,
  });
}

export function openNewCanvas(fc, width, height) {
  const w = Math.max(width || DEFAULT_CANVAS_WIDTH, 50);
  const h = Math.max(height || DEFAULT_CANVAS_HEIGHT, 50);
  fc.clear();
  fc.setWidth(w);
  fc.setHeight(h);
  fc.setBackgroundColor('#FFFFFF', () => fc.renderAll());
  return { width: w, height: h };
}

export function loadDesignPayload(fc, template) {
  const { width, height } = parseDimensions(template.dimensions);
  openNewCanvas(fc, width, height);
  const original = { width, height };

  let layoutRaw = template.layout || '{}';
  if (typeof layoutRaw === 'object') layoutRaw = JSON.stringify(layoutRaw);

  const isEmpty =
    !layoutRaw
    || layoutRaw === '{}'
    || layoutRaw === '{"objects":[],"background":"#FFFFFF"}';

  if (isEmpty) {
    return Promise.resolve(original);
  }

  let parsed;
  try {
    parsed = JSON.parse(layoutRaw);
  } catch {
    return Promise.resolve(original);
  }

  if (parsed.objects) {
    parsed.objects = parsed.objects.map((obj) => {
      if (obj.type === 'image' && obj.src) {
        return { ...obj, crossOrigin: 'anonymous' };
      }
      return obj;
    });
  }

  return new Promise((resolve) => {
    fc.loadFromJSON(parsed, () => {
      relinkButtonObjects(fc);
      sanitizeCanvasForFabricExport(fc);
      fc.getObjects().forEach(applyLoadedObjectRules);
      if (parsed.background) {
        fc.setBackgroundColor(parsed.background, () => fc.renderAll());
      }
      fc.renderAll();
      resolve(original);
    });
  });
}

export function fitCanvasToContainer(fc, containerEl, original, manualZoom = null) {
  if (!containerEl || !original?.width) return 1;
  const maxWidth = containerEl.clientWidth - 40;
  const maxHeight = containerEl.clientHeight - 80;
  const fitScale = Math.min(maxHeight / original.height, maxWidth / original.width, 1);
  const baseZoom = fitScale > 0 ? fitScale : 1;
  const zoom = manualZoom != null ? manualZoom * baseZoom : baseZoom;
  fc.setZoom(zoom);
  fc.setWidth(original.width * zoom);
  fc.setHeight(original.height * zoom);
  fc.renderAll();
  return zoom;
}

export function setCanvasZoom(fc, original, zoomPercent, containerEl) {
  const z = Math.max(0.1, Math.min(2, zoomPercent / 100));
  if (containerEl && original?.width) {
    const maxWidth = containerEl.clientWidth - 40;
    const maxHeight = containerEl.clientHeight - 80;
    const fitScale = Math.min(maxHeight / original.height, maxWidth / original.width, 1);
    const zoom = z * (fitScale > 0 ? fitScale : 1);
    fc.setZoom(zoom);
    fc.setWidth(original.width * zoom);
    fc.setHeight(original.height * zoom);
  } else {
    fc.setZoom(z);
    fc.setWidth(original.width * z);
    fc.setHeight(original.height * z);
  }
  fc.renderAll();
  return z;
}

export function exportCanvasPng(fc, multiplier = 1) {
  return fc.toDataURL({ format: 'png', multiplier, quality: 1 });
}

export function addText(fc, text = 'Text', options = {}) {
  const tb = new fabric.Textbox(text, {
    name: 'text',
    niceName: options.niceName || 'text',
    left: options.left ?? 50,
    top: options.top ?? 50,
    width: options.width ?? 300,
    fontSize: options.fontSize ?? 32,
    fill: options.fill ?? '#000000',
    fontFamily: options.fontFamily || 'Open Sans',
    textAlign: options.textAlign || 'left',
    senderNameChecked: options.senderNameChecked || false,
    styles: options.styles ?? {},
    ...options,
  });
  fc.add(tb);
  fc.setActiveObject(tb);
  fc.renderAll();
  return tb;
}

export function updateTextObject(obj, patch) {
  if (!obj) return;
  obj.set(patch);
  obj.setCoords();
}

export function addRect(fc, options = {}) {
  const rect = new fabric.Rect({
    name: 'shape',
    niceName: 'shape',
    left: 100,
    top: 100,
    width: 200,
    height: 80,
    fill: options.fill || '#008000',
    rx: options.rx || 0,
    ry: options.ry || 0,
    ...options,
  });
  fc.add(rect);
  fc.setActiveObject(rect);
  fc.renderAll();
  return rect;
}

export function addCircle(fc, options = {}) {
  const r = options.radius || 60;
  const circle = new fabric.Circle({
    name: 'shape',
    niceName: 'shape',
    left: options.left ?? 100,
    top: options.top ?? 100,
    radius: r,
    fill: options.fill || '#008000',
    ...options,
  });
  fc.add(circle);
  fc.setActiveObject(circle);
  fc.renderAll();
  return circle;
}

export function addTriangle(fc, options = {}) {
  const tri = new fabric.Triangle({
    name: 'shape',
    niceName: 'shape',
    left: options.left ?? 120,
    top: options.top ?? 120,
    width: options.width ?? 120,
    height: options.height ?? 120,
    fill: options.fill || '#008000',
    ...options,
  });
  fc.add(tri);
  fc.setActiveObject(tri);
  fc.renderAll();
  return tri;
}

export function addEditableGroup(fc, options = {}) {
  const rect = new fabric.Rect({
    left: 0,
    top: 0,
    width: 685,
    height: 235,
    fill: '#FFFFFF',
    rx: 10,
    ry: 10,
  });
  const text = new fabric.Textbox('Text Message', {
    left: 15,
    top: 15,
    width: 400,
    fontSize: 40,
    fontWeight: 'bold',
    fill: '#000000',
    fontFamily: 'Roboto',
    styles: {},
  });
  const group = new fabric.Group([rect, text], {
    name: 'editable',
    niceName: 'shape',
    left: options.left ?? 15,
    top: options.top ?? 755,
    ...options,
  });
  group.lockMovementX = true;
  group.hasControls = false;
  fc.add(group);
  fc.setActiveObject(group);
  fc.renderAll();
  return group;
}

export function addEntryField(fc, options = {}) {
  const rect = new fabric.Rect({
    name: 'entryfield',
    niceName: 'shape',
    left: options.left ?? 50,
    top: options.top ?? 600,
    width: options.width ?? 620,
    height: options.height ?? 80,
    fill: '#FFFFFF',
    stroke: '#CCCCCC',
    strokeWidth: 1,
    rx: 4,
    ry: 4,
    ...options,
  });
  rect.lockMovementX = true;
  rect.hasControls = false;
  fc.add(rect);
  fc.setActiveObject(rect);
  fc.renderAll();
  return rect;
}

export function addOrgBanner(fc, options = {}) {
  const bannerRect = new fabric.Rect({
    left: -360,
    top: -63,
    width: 720,
    height: 125,
    fill: 'rgba(10,79,120,1)',
  });
  const title = new fabric.Textbox('Organization Name', {
    left: -335,
    top: -38,
    width: 500,
    fontSize: 40,
    fill: '#FFFFFF',
    fontFamily: 'Roboto',
    styles: {},
  });
  const sub = new fabric.Textbox('Address line', {
    left: -335,
    top: 12,
    width: 500,
    fontSize: 24,
    fill: '#FFFFFF',
    fontFamily: 'Roboto',
    styles: {},
  });
  const group = new fabric.Group([bannerRect, title, sub], {
    name: 'orgbanner',
    niceName: 'shape',
    left: 0,
    top: 0,
    lockMovementX: true,
    lockMovementY: true,
    hasControls: false,
    ...options,
  });
  fc.add(group);
  fc.setActiveObject(group);
  fc.renderAll();
  return group;
}

let buttonCounter = 0;

function nextButtonIndex(fc) {
  return relinkButtonObjects(fc) + 1;
}

export function addButton(fc, label = 'Button', options = {}) {
  if (countButtons(fc) >= MAX_BUTTONS) {
    return null;
  }
  const shape = options.shape === 'circle' ? 'circle' : 'rect';
  if (shape === 'circle') {
    return addCircleButton(fc, label, options);
  }
  return addRectButton(fc, label, options);
}

function addRectButton(fc, label, options = {}) {
  const idx = nextButtonIndex(fc);
  buttonCounter = Math.max(buttonCounter, idx);
  const rect = new fabric.Rect({
    name: 'rect',
    niceName: `button rect ${idx}`,
    left: options.left || 80,
    top: options.top || 400,
    width: options.width || 220,
    height: options.height || 56,
    fill: options.fill || '#008000',
    rx: options.rx ?? 20,
    ry: options.ry ?? 20,
    action: options.action || 'link:https://example.com',
  });
  const text = new fabric.Text(label, {
    name: 'text',
    niceName: `button text ${idx}`,
    fontSize: options.fontSize || 24,
    fill: options.textFill || '#FFFFFF',
    fontFamily: options.fontFamily || 'Open Sans',
    styles: {},
    originX: 'center',
    originY: 'center',
  });
  rect.textObj = text;
  text.textObj = rect;
  text.left = rect.left + rect.width / 2;
  text.top = rect.top + rect.height / 2;
  fc.add(rect);
  fc.add(text);
  fc.setActiveObject(rect);
  fc.renderAll();
  return { rect, text };
}

function addCircleButton(fc, label, options = {}) {
  const idx = nextButtonIndex(fc);
  buttonCounter = Math.max(buttonCounter, idx);
  const size = options.width || 120;
  const circle = new fabric.Circle({
    name: 'circle',
    niceName: `button circle ${idx}`,
    left: options.left || 80,
    top: options.top || 400,
    radius: size / 2,
    fill: options.fill || '#008000',
    action: options.action || 'link:https://example.com',
  });
  const text = new fabric.Text(label, {
    name: 'text',
    niceName: `button text ${idx}`,
    fontSize: options.fontSize || 20,
    fill: options.textFill || '#FFFFFF',
    fontFamily: options.fontFamily || 'Open Sans',
    styles: {},
    originX: 'center',
    originY: 'center',
  });
  circle.textObj = text;
  text.textObj = circle;
  text.left = circle.left + size / 2;
  text.top = circle.top + size / 2;
  fc.add(circle);
  fc.add(text);
  fc.setActiveObject(circle);
  fc.renderAll();
  return { rect: circle, text };
}

export function setBackgroundColor(fc, color) {
  fc.setBackgroundColor(color, () => fc.renderAll());
}

export function loadImageFromUrl(url) {
  return new Promise((resolve, reject) => {
    fabric.Image.fromURL(
      url,
      (img) => {
        if (img) resolve(img);
        else reject(new Error('Image load failed'));
      },
      { crossOrigin: 'anonymous' }
    );
  });
}

export function loadStickerFromUrl(url, isSvg = false) {
  if (!isSvg) {
    return loadImageFromUrl(url);
  }
  return new Promise((resolve, reject) => {
    fabric.loadSVGFromURL(url, (objects, opts) => {
      if (!objects || !objects.length) {
        reject(new Error('SVG load failed'));
        return;
      }
      const grp = fabric.util.groupSVGElements(objects, opts);
      resolve(grp);
    }, null, { crossOrigin: 'anonymous' });
  });
}

export function addImageToCanvas(fc, url, options = {}) {
  return loadImageFromUrl(url).then((img) => {
    img.set({
      name: options.name || 'image',
      niceName: 'image',
      left: options.left ?? 50,
      top: options.top ?? 50,
      uploadUrl: url,
      ...options,
    });
    if (options.scale) {
      img.scale(options.scale);
    }
    fc.add(img);
    fc.setActiveObject(img);
    fc.renderAll();
    return img;
  });
}

function rasterizeObjectToDataUrl(obj) {
  if (typeof obj.toDataURL === 'function') {
    return obj.toDataURL({ format: 'png', quality: 0.8 });
  }
  const w = Math.max(1, Math.ceil((obj.width || obj.radius * 2 || 100) * (obj.scaleX || 1)));
  const h = Math.max(1, Math.ceil((obj.height || obj.radius * 2 || 100) * (obj.scaleY || 1)));
  const offscreen = document.createElement('canvas');
  offscreen.width = w;
  offscreen.height = h;
  const ctx = offscreen.getContext('2d');
  const top = obj.top;
  const left = obj.left;
  const angle = obj.angle || 0;
  obj.angle = 0;
  obj.top = 0;
  obj.left = 0;
  obj.setCoords();
  if (typeof obj.render === 'function') {
    obj.render(ctx);
  }
  obj.top = top;
  obj.left = left;
  obj.angle = angle;
  obj.setCoords();
  return offscreen.toDataURL('image/png', 0.8);
}

/** Rasterize and upload canvas images/stickers/shapes/icons for imageLayout (legacy saveEditedImages) */
export async function prepareImageDataForSave(fabricCanvas, uploadFn) {
  const list = [];
  fabricCanvas.forEachObject((obj) => {
    if (shouldIncludeInImageUpload(obj)) list.push(obj);
  });

  const imageData = [];
  let zorder = 0;

  for (let i = 0; i < list.length; i += 1) {
    const obj = list[i];
    obj.zorder = zorder;
    zorder += 1;

    const existing = obj.uploadUrl || obj.url || '';
    if (isServerUploadUrl(existing)) {
      imageData.push({ obj, url: existing });
    } else {
      const dataUrl = rasterizeObjectToDataUrl(obj);
      const path = await uploadFn(dataUrl);
      obj.uploadUrl = path;
      obj.url = path;
      imageData.push({ obj, url: path });
    }
  }

  return imageData;
}

export async function addStickerToCanvas(fc, category, index) {
  const url = stickerUrl(category, index);
  const isSvg = category.type === 'svg';
  const obj = await loadStickerFromUrl(url, isSvg);
  const targetHeight = fc.getHeight() * 0.4;

  obj.set({
    name: 'sticker',
    niceName: 'sticker',
    imagetype: category.type,
    uploadUrl: url,
    opacity: 0.9,
    fill: isSvg ? '#000000' : obj.fill,
  });

  if (typeof obj.scaleToHeight === 'function') {
    obj.scaleToHeight(targetHeight);
  } else {
    const h = obj.height * (obj.scaleY || 1);
    if (h > 0) {
      const scale = targetHeight / h;
      obj.scale(scale);
    }
  }

  fc.add(obj);
  if (typeof obj.center === 'function') {
    obj.center();
  }
  obj.setCoords();
  fc.setActiveObject(obj);
  fc.renderAll();
  return obj;
}

export function setObjectFill(obj, color) {
  if (!obj) return;
  if (obj.type === 'group' && obj.getObjects) {
    obj.getObjects().forEach((child) => {
      if (child.type === 'rect' || child.type === 'circle' || child.type === 'text' || child.type === 'textbox') {
        child.set('fill', color);
      }
    });
  } else {
    obj.set('fill', color);
  }
  obj.setCoords();
}

export { fabric };
